import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Description, PlayArrow, Download } from '@mui/icons-material';
import { CampaignContact, CallLog } from '../../store/api/outboundCallingApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getApiUrl } from '../../config/api';

interface ContactStatusTabProps {
  contacts: CampaignContact[];
  isLoading: boolean;
  campaignId: number;
  callLogs: CallLog[];
  campaignStatus?: 'active' | 'paused' | 'stopped' | 'completed';
  maxRetries?: number;
}

const ContactStatusTab: React.FC<ContactStatusTabProps> = ({ contacts, isLoading, campaignId, callLogs, campaignStatus, maxRetries }) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [selectedContact, setSelectedContact] = useState<CampaignContact | null>(null);
  const [transcriptDialogOpen, setTranscriptDialogOpen] = useState(false);
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);

  const getDisplayStatus = (contact: CampaignContact): string => {
    const latestCallLog = getContactCallLog(contact);
    const callStatus = latestCallLog?.call_status?.toLowerCase();
    
    // Completed - if the latest call has completed status
    if (callStatus && ['completed', 'successful', 'finished'].includes(callStatus)) {
      return 'completed';
    }
    
    // Retry_exceeded - if max retries exceeded and call is not completed
    if (maxRetries !== undefined && contact.current_attempt >= maxRetries) {
      return 'retry_exceeded';
    }
    
    // Inprogress - if we don't know the status OR it is in Pending status
    // (no call log, or call_status is null/unknown, or final_status is 'pending')
    if (!latestCallLog || !callStatus || callStatus === 'unknown' || contact.final_status === 'pending') {
      return 'inprogress';
    }
    
    // Default to inprogress for any other case
    return 'inprogress';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'retry_exceeded':
        return 'error';
      case 'inprogress':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds: number | undefined) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getContactCallLog = (contact: CampaignContact) => {
    if (!callLogs || callLogs.length === 0) return null;
    
    // Find the most recent call log for this contact
    // Handle potential type mismatch (string vs number) and debug call log 31
    const callLog31 = callLogs.find(log => log.id === 31);
    if (callLog31) {
      console.log('[DEBUG] Call log 31 found:', {
        contact_id: callLog31.contact_id,
        contact_id_type: typeof callLog31.contact_id,
        transcript: !!callLog31.transcript,
        recording_url: !!callLog31.recording_url,
        currentContactId: contact.id,
        currentContactId_type: typeof contact.id,
        matches: String(callLog31.contact_id) === String(contact.id)
      });
    }
    
    const contactCallLogs = callLogs
      .filter(log => {
        // Handle potential type mismatch - compare as strings to be safe
        return String(log.contact_id) === String(contact.id);
      })
      .sort((a, b) => {
        // Sort by call_started_at if available, otherwise by created_at
        const aTime = a.call_started_at ? new Date(a.call_started_at).getTime() : new Date(a.created_at).getTime();
        const bTime = b.call_started_at ? new Date(b.call_started_at).getTime() : new Date(b.created_at).getTime();
        return bTime - aTime;
      });
    
    if (contactCallLogs.length > 0) {
      const latest = contactCallLogs[0];
      console.log(`[DEBUG] Found ${contactCallLogs.length} call log(s) for contact ${contact.id} (${contact.first_name} ${contact.last_name}). Latest call log:`, {
        logId: latest.id,
        isLog31: latest.id === 31,
        transcript: !!latest.transcript,
        transcript_value: latest.transcript ? (latest.transcript.substring(0, 50) + '...') : 'null',
        recording_url: !!latest.recording_url,
        recording_url_value: latest.recording_url || 'null',
        allCallLogIds: contactCallLogs.map(log => ({ 
          id: log.id, 
          isLog31: log.id === 31,
          hasTranscript: !!log.transcript,
          hasRecording: !!log.recording_url,
          call_started_at: log.call_started_at,
          created_at: log.created_at
        }))
      });
    } else {
      console.log(`[DEBUG] No call logs found for contact ${contact.id} (${contact.first_name} ${contact.last_name})`);
    }
    
    return contactCallLogs.length > 0 ? contactCallLogs[0] : null;
  };

  const handleTranscriptClick = (contact: CampaignContact) => {
    console.log('[DEBUG] Transcript clicked for contact:', {
      contactId: contact.id,
      contactName: `${contact.first_name} ${contact.last_name}`
    });
    const callLog = getContactCallLog(contact);
    console.log('[DEBUG] Call log for transcript dialog:', {
      callLogId: callLog?.id,
      isLog31: callLog?.id === 31,
      hasTranscript: !!callLog?.transcript,
      transcript: callLog?.transcript ? callLog.transcript.substring(0, 100) + '...' : 'null'
    });
    setSelectedContact(contact);
    setTranscriptDialogOpen(true);
  };

  const handleRecordingClick = (contact: CampaignContact) => {
    console.log('[DEBUG] Recording clicked for contact:', {
      contactId: contact.id,
      contactName: `${contact.first_name} ${contact.last_name}`
    });
    const callLog = getContactCallLog(contact);
    console.log('[DEBUG] Call log for recording dialog:', {
      callLogId: callLog?.id,
      isLog31: callLog?.id === 31,
      hasRecording: !!callLog?.recording_url,
      recordingUrl: callLog?.recording_url || 'null'
    });
    setSelectedContact(contact);
    setRecordingDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (contacts.length === 0) {
    return (
      <Alert severity="info">
        No contacts found for this campaign.
      </Alert>
    );
  }

  const isDownloadEnabled = campaignStatus === 'stopped' || campaignStatus === 'completed';

  const handleDownloadCSV = async () => {
    try {
      if (!token) {
        alert('Please log in to download CSV');
        return;
      }

      const response = await fetch(
        getApiUrl(`/api/v1/outbound-calling/campaigns/${campaignId}/contacts/csv`),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      console.log('CSV download response status:', response.status, response.statusText);
      console.log('CSV download response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Failed to download CSV';
        try {
          const errorText = await response.text();
          console.error('Error response text:', errorText);
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          } catch {
            errorMessage = errorText || response.statusText || errorMessage;
          }
        } catch {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        console.error('CSV download error:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage
        });
        throw new Error(errorMessage);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      console.log('Content-Disposition header:', contentDisposition);
      let filename = 'contacts.csv';
      if (contentDisposition) {
        // Try RFC 5987 format first: filename*=UTF-8''encoded-filename
        const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (rfc5987Match) {
          try {
            filename = decodeURIComponent(rfc5987Match[1]);
            console.log('Extracted filename from RFC 5987 format:', filename);
          } catch (e) {
            console.warn('Failed to decode RFC 5987 filename:', e);
          }
        } else {
          // Fall back to standard format: filename="filename"
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
            console.log('Extracted filename from standard format:', filename);
          }
        }
      }
      console.log('Final filename to use:', filename);

      // Get response as text first to debug
      const responseText = await response.text();
      console.log('CSV response text length:', responseText.length);
      console.log('CSV response text preview (first 500 chars):', responseText.substring(0, 500));
      
      // Check if response is actually CSV or an error (check for JSON error format)
      if (responseText.trim().startsWith('{') && (responseText.includes('"error"') || responseText.includes('"detail"'))) {
        console.error('CSV response appears to be a JSON error:', responseText);
        try {
          const errorData = JSON.parse(responseText);
          throw new Error(errorData.detail || errorData.error || 'Invalid CSV response from server');
        } catch (e) {
          throw new Error('Invalid CSV response from server');
        }
      }
      
      // Check if response is just "null" as a string
      if (responseText.trim() === 'null' || responseText.trim() === '') {
        console.error('CSV response is null or empty:', responseText);
        throw new Error('CSV file is empty. No contacts found for this campaign.');
      }

      // Create blob from text
      const blob = new Blob([responseText], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Error downloading CSV:', error);
      const errorMessage = error?.message || 'Failed to download CSV. Please try again.';
      alert(errorMessage);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Contact Status ({contacts.length} contacts)
        </Typography>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleDownloadCSV}
          disabled={!isDownloadEnabled}
          color="primary"
        >
          Download CSV
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Contact Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Attempt</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Call Time</TableCell>
              <TableCell>Last Call Duration</TableCell>
              <TableCell>Last Call ID</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell>
                  {contact.first_name} {contact.last_name}
                </TableCell>
                <TableCell>{contact.phone}</TableCell>
                <TableCell>{contact.current_attempt}</TableCell>
                <TableCell>
                  <Chip
                    label={getDisplayStatus(contact).toUpperCase()}
                    color={getStatusColor(getDisplayStatus(contact)) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(contact.last_call_at)}</TableCell>
                <TableCell>
                  {formatDuration(getContactCallLog(contact)?.call_duration_seconds)}
                </TableCell>
                <TableCell>
                  {getContactCallLog(contact)?.synthflow_call_id || 'N/A'}
                </TableCell>
                <TableCell>
                  {(() => {
                    const contactCallLog = getContactCallLog(contact);
                    return (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleTranscriptClick(contact)}
                          title="View Transcript"
                          disabled={!contactCallLog?.transcript}
                        >
                          <Description />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRecordingClick(contact)}
                          title="View Recording"
                          disabled={!contactCallLog?.recording_url}
                        >
                          <PlayArrow />
                        </IconButton>
                      </>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Transcript Dialog */}
      <Dialog
        open={transcriptDialogOpen}
        onClose={() => setTranscriptDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Call Transcript - {selectedContact?.first_name} {selectedContact?.last_name}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const contactCallLog = selectedContact ? getContactCallLog(selectedContact) : null;
            console.log('[DEBUG] Transcript dialog rendering:', {
              selectedContactId: selectedContact?.id,
              selectedContactName: selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : 'null',
              callLogId: contactCallLog?.id,
              isLog31: contactCallLog?.id === 31,
              hasTranscript: !!contactCallLog?.transcript,
              transcriptLength: contactCallLog?.transcript?.length || 0
            });
            return contactCallLog?.transcript ? (
              <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                {contactCallLog.transcript}
              </Box>
            ) : (
              <Typography color="text.secondary">
                No transcript available for this contact.
              </Typography>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranscriptDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recording Dialog */}
      <Dialog
        open={recordingDialogOpen}
        onClose={() => setRecordingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Call Recording - {selectedContact?.first_name} {selectedContact?.last_name}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const contactCallLog = selectedContact ? getContactCallLog(selectedContact) : null;
            console.log('[DEBUG] Recording dialog rendering:', {
              selectedContactId: selectedContact?.id,
              selectedContactName: selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : 'null',
              callLogId: contactCallLog?.id,
              isLog31: contactCallLog?.id === 31,
              hasRecording: !!contactCallLog?.recording_url,
              recordingUrl: contactCallLog?.recording_url || 'null'
            });
            return contactCallLog?.recording_url ? (
              <Box>
                <audio controls style={{ width: '100%' }}>
                  <source src={contactCallLog.recording_url} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    href={contactCallLog.recording_url}
                    download
                  >
                    Download Recording
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography color="text.secondary">
                No recording available for this contact.
              </Typography>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordingDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactStatusTab;
